import { useTranslation, useAuth } from '@/context/AuthContext';
import { useState, useEffect, useCallback, useRef } from 'react';
import { getDayPlans, addActivity, updateActivity, deleteActivity, addTransportSegment, updateTransportSegment, deleteTransportSegment, getStressScore, generateActivityAudioGuide, reorderActivities } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Plus, Trash2, Sun, Sunset, Moon, Car, Clock, MapPin, Pencil, Plane, Train, Volume2, Loader2, Baby, Navigation, GripVertical, Bus, Ship, Route, ArrowRight, Square, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DayAIChatModal from '@/components/DayAIChatModal';
import { useAudioPlayer } from '@/context/AudioPlayerContext';

export const DayPlanner = ({ tripId, trip }) => {
  const { t } = useTranslation();
  const [dayPlans, setDayPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await getDayPlans(tripId);
      setDayPlans(res.data);
      if (res.data.length > 0 && !selectedDay) {
        setSelectedDay(res.data[0].day_plan_id);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [tripId, selectedDay]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="h-48 bg-[#E2E8F0] rounded-2xl animate-pulse" />;

  const currentPlan = dayPlans.find(dp => dp.day_plan_id === selectedDay);

  return (
    <div className="space-y-4" data-testid="day-planner">
      {dayPlans.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {dayPlans.map((dp, i) => {
            const d = new Date(dp.date);
            const isSelected = dp.day_plan_id === selectedDay;
            const actCount = (dp.activities || []).length + (dp.transport_segments || []).length;
            return (
              <button
                key={dp.day_plan_id}
                onClick={() => setSelectedDay(dp.day_plan_id)}
                className={`flex-shrink-0 px-4 py-3 rounded-xl transition-all flex flex-col items-center min-w-[60px] ${
                  isSelected ? 'bg-gradient-to-b from-[#3A86FF] to-[#2563EB] text-white shadow-lg scale-105' : 'bg-white border border-[#E2E8F0] text-[#64748B] hover:border-[#3A86FF]/30'
                }`}
              >
                <span className="text-[10px] uppercase tracking-wide opacity-80">{d.toLocaleDateString(undefined, { weekday: 'short' })}</span>
                <span className="text-lg font-bold">{d.getDate()}</span>
                {actCount > 0 && <span className="text-[9px] opacity-70">{actCount}</span>}
              </button>
            );
          })}
        </div>
      )}

      {dayPlans.length === 0 ? (
        <Card className="border border-[#E2E8F0]/40 rounded-2xl">
          <CardContent className="p-10 text-center">
            <Clock className="w-12 h-12 text-[#E2E8F0] mx-auto mb-3" strokeWidth={1.2} />
            <h3 className="text-lg font-medium text-[#1E293B] mb-2">{t('planner.noDays')}</h3>
            <p className="text-xs text-[#64748B]">Définissez les dates du voyage</p>
          </CardContent>
        </Card>
      ) : currentPlan && (
        <DayContent key={currentPlan.day_plan_id} plan={currentPlan} tripId={tripId} onRefresh={load} />
      )}
    </div>
  );
};

// Sortable Item Component
function SortableItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {children({ listeners, isDragging })}
    </div>
  );
}

function DayContent({ plan, tripId, onRefresh }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { startAudioGuide, isGenerating: audioGenerating, isOpen: audioPlayerOpen } = useAudioPlayer();
  
  const [actForm, setActForm] = useState({ title: '', time_block: 'morning', location: '', start_time: '', end_time: '', notes: '' });
  const [transForm, setTransForm] = useState({ from_location: '', to_location: '', mode: 'driving', departure_time: '', arrival_time: '' });
  const [actOpen, setActOpen] = useState(false);
  const [transOpen, setTransOpen] = useState(false);
  
  const [editingActivity, setEditingActivity] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editingTransport, setEditingTransport] = useState(null);
  const [editTransOpen, setEditTransOpen] = useState(false);
  
  const [detailItem, setDetailItem] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  
  const [timeline, setTimeline] = useState([]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Build timeline
  useEffect(() => {
    const items = [];
    (plan.activities || []).forEach(act => {
      items.push({
        type: 'activity',
        id: act.activity_id,
        time: act.start_time || getBlockTime(act.time_block),
        order: act.order || 0,
        data: act
      });
    });
    (plan.transport_segments || []).forEach(seg => {
      items.push({
        type: 'transport',
        id: seg.segment_id,
        time: seg.departure_time || '',
        order: seg.order || 99,
        data: seg
      });
    });
    items.sort((a, b) => {
      if (a.time && b.time) return a.time.localeCompare(b.time);
      return a.order - b.order;
    });
    setTimeline(items);
  }, [plan]);

  const getBlockTime = (block) => {
    if (block === 'morning') return '09:00';
    if (block === 'afternoon') return '14:00';
    if (block === 'evening') return '19:00';
    return '12:00';
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    
    const oldIndex = timeline.findIndex(i => i.id === active.id);
    const newIndex = timeline.findIndex(i => i.id === over.id);
    
    const newTimeline = arrayMove(timeline, oldIndex, newIndex);
    setTimeline(newTimeline);
    
    // Update order in DB
    const updates = newTimeline.map((item, idx) => ({
      id: item.id,
      type: item.type,
      order: idx
    }));
    
    for (const u of updates) {
      try {
        if (u.type === 'activity') {
          await updateActivity(u.id, { order: u.order });
        } else {
          await updateTransportSegment(u.id, { order: u.order });
        }
      } catch (e) { console.error(e); }
    }
    
    toast.success('Ordre mis à jour');
  };

  const handlePlayAudioGuide = async (activity) => {
    if (audioGenerating) return;
    
    // Use global audio player with activity name and location
    await startAudioGuide(
      {
        name: activity.title || 'Activité',
        category: activity.type || 'activity',
        description: activity.notes || activity.location || '',
      },
      {
        version: 'medium',
        lang: user?.language || 'fr',
      }
    );
  };

  const handleStopAudio = () => {
    // Not needed anymore - global player has close button
  };

  const handleAddActivity = async () => {
    if (!actForm.title) return;
    await addActivity(plan.day_plan_id, actForm);
    setActForm({ title: '', time_block: 'morning', location: '', start_time: '', end_time: '', notes: '' });
    setActOpen(false);
    toast.success('Activité ajoutée');
    onRefresh();
  };

  const handleEditActivity = (act) => {
    setEditingActivity({ ...act });
    setEditOpen(true);
  };

  const handleSaveActivity = async () => {
    if (!editingActivity?.title) return;
    await updateActivity(editingActivity.activity_id, editingActivity);
    setEditOpen(false);
    setEditingActivity(null);
    toast.success('Activité mise à jour');
    onRefresh();
  };

  const handleEditTransport = (seg) => {
    setEditingTransport({ ...seg });
    setEditTransOpen(true);
  };

  const handleSaveTransport = async () => {
    if (!editingTransport) return;
    await updateTransportSegment(editingTransport.segment_id, editingTransport);
    setEditTransOpen(false);
    setEditingTransport(null);
    toast.success('Transport mis à jour');
    onRefresh();
  };

  const handleAddTransport = async () => {
    if (!transForm.from_location || !transForm.to_location) return;
    await addTransportSegment(plan.day_plan_id, transForm);
    setTransForm({ from_location: '', to_location: '', mode: 'driving', departure_time: '', arrival_time: '' });
    setTransOpen(false);
    toast.success('Transport ajouté');
    onRefresh();
  };

  const openNavigation = (loc) => window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(loc)}`, '_blank');

  const transportModes = [
    { value: 'driving', label: 'Voiture', icon: Car },
    { value: 'plane', label: 'Avion', icon: Plane },
    { value: 'train', label: 'Train', icon: Train },
    { value: 'bus', label: 'Bus', icon: Bus },
    { value: 'ferry', label: 'Ferry', icon: Ship },
  ];

  const getModeIcon = (mode) => transportModes.find(t => t.value === mode)?.icon || Car;
  const getModeLabel = (mode) => transportModes.find(t => t.value === mode)?.label || mode;

  const timeBlocks = [
    { key: 'morning', icon: Sun, label: 'Matin', color: '#F59E0B' },
    { key: 'afternoon', icon: Sunset, label: 'Après-midi', color: '#3A86FF' },
    { key: 'evening', icon: Moon, label: 'Soir', color: '#8B5CF6' },
  ];

  const dateStr = new Date(plan.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <Card className="border border-[#E2E8F0]/40 rounded-2xl overflow-hidden">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-base font-bold text-[#1E293B] capitalize">{dateStr}</h3>
            <p className="text-[10px] text-[#94A3B8]">{timeline.length} éléments • Glissez pour réorganiser</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setAiChatOpen(true)} className="rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white h-9 text-xs">
              <Sparkles className="w-3.5 h-3.5 mr-1" /> Chat IA
            </Button>
            <Dialog open={actOpen} onOpenChange={setActOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="rounded-xl bg-[#3A86FF] hover:bg-[#2563EB] text-white h-9 text-xs">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Activité
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Ajouter une activité</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-4">
                  <div>
                    <Label>Titre *</Label>
                    <Input value={actForm.title} onChange={e => setActForm({...actForm, title: e.target.value})} className="mt-1" placeholder="Ex: Visite Tour Eiffel" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Créneau</Label>
                      <Select value={actForm.time_block} onValueChange={v => setActForm({...actForm, time_block: v})}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>{timeBlocks.map(b => <SelectItem key={b.key} value={b.key}>{b.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Lieu</Label>
                      <Input value={actForm.location} onChange={e => setActForm({...actForm, location: e.target.value})} className="mt-1" placeholder="Adresse" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Début</Label><Input type="time" value={actForm.start_time} onChange={e => setActForm({...actForm, start_time: e.target.value})} className="mt-1" /></div>
                    <div><Label>Fin</Label><Input type="time" value={actForm.end_time} onChange={e => setActForm({...actForm, end_time: e.target.value})} className="mt-1" /></div>
                  </div>
                  <Button onClick={handleAddActivity} className="w-full rounded-full bg-[#3A86FF] text-white">Ajouter</Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={transOpen} onOpenChange={setTransOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="rounded-xl h-9 text-xs">
                  <Route className="w-3.5 h-3.5 mr-1" /> Transport
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Ajouter un transport</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Départ</Label><Input value={transForm.from_location} onChange={e => setTransForm({...transForm, from_location: e.target.value})} className="mt-1" /></div>
                    <div><Label>Arrivée</Label><Input value={transForm.to_location} onChange={e => setTransForm({...transForm, to_location: e.target.value})} className="mt-1" /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>Mode</Label>
                      <Select value={transForm.mode} onValueChange={v => setTransForm({...transForm, mode: v})}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>{transportModes.map(m => <SelectItem key={m.value} value={m.value}><span className="flex items-center gap-2"><m.icon className="w-4 h-4" />{m.label}</span></SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Départ</Label><Input type="time" value={transForm.departure_time} onChange={e => setTransForm({...transForm, departure_time: e.target.value})} className="mt-1" /></div>
                    <div><Label>Arrivée</Label><Input type="time" value={transForm.arrival_time} onChange={e => setTransForm({...transForm, arrival_time: e.target.value})} className="mt-1" /></div>
                  </div>
                  <Button onClick={handleAddTransport} className="w-full rounded-full bg-[#3A86FF] text-white">Ajouter</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Timeline with DnD */}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={timeline.map(i => i.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {timeline.length === 0 ? (
                <div className="border border-dashed border-[#E2E8F0] rounded-xl p-8 text-center">
                  <Clock className="w-8 h-8 text-[#CBD5E1] mx-auto mb-2" />
                  <p className="text-sm text-[#94A3B8]">Aucune activité</p>
                </div>
              ) : (
                timeline.map((item) => {
                  const isActivity = item.type === 'activity';
                  const d = item.data;
                  const ModeIcon = isActivity ? MapPin : getModeIcon(d.mode);
                  const blockColor = isActivity ? (timeBlocks.find(b => b.key === d.time_block)?.color || '#3A86FF') : '#10B981';
                  const isPlaying = audioPlayerOpen; // Global player is open
                  
                  return (
                    <SortableItem key={item.id} id={item.id}>
                      {({ listeners, isDragging }) => (
                        <div 
                          className={`relative flex gap-3 p-3 rounded-xl transition-all group ${
                            isActivity ? 'bg-white border border-[#E2E8F0]/60' : 'bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200/50'
                          } ${isPlaying ? 'ring-2 ring-[#3A86FF]' : ''} ${isDragging ? 'shadow-lg ring-2 ring-[#3A86FF]/30' : 'hover:shadow-md'}`}
                        >
                          {/* Drag handle */}
                          <div 
                            {...listeners} 
                            className="flex-shrink-0 cursor-grab active:cursor-grabbing p-1 -ml-1 hover:bg-gray-100 rounded touch-none"
                            style={{ touchAction: 'none' }}
                          >
                            <GripVertical className="w-4 h-4 text-[#94A3B8]" />
                          </div>

                          {/* Clickable content area */}
                          <div 
                            className="flex flex-1 gap-3 cursor-pointer"
                            onClick={() => { 
                              console.log('Activity clicked:', d); 
                              setDetailItem(d); 
                              setDetailOpen(true); 
                            }}
                          >
                            {/* Time */}
                            <div className="flex-shrink-0 w-12 text-center">
                              <span className="text-xs font-bold" style={{ color: blockColor }}>{item.time || '--:--'}</span>
                              {d.end_time || d.arrival_time ? <span className="text-[9px] text-[#94A3B8] block">{d.end_time || d.arrival_time}</span> : null}
                            </div>

                            {/* Icon */}
                            <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${blockColor}15` }}>
                              <ModeIcon className="w-4 h-4" style={{ color: blockColor }} />
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#1E293B]">
                                {isActivity ? d.title : <span className="flex items-center gap-1">{d.from_location} <ArrowRight className="w-3 h-3 text-emerald-500" /> {d.to_location}</span>}
                              </p>
                              {isActivity && d.location && <p className="text-[10px] text-[#64748B] flex items-center gap-1"><MapPin className="w-3 h-3" />{d.location}</p>}
                              {!isActivity && <Badge className="text-[9px] mt-1 border-0 bg-emerald-100 text-emerald-700">{getModeLabel(d.mode)}</Badge>}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {isActivity && d.location && (
                              <>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    handlePlayAudioGuide(d); 
                                  }} 
                                  disabled={audioGenerating} 
                                  className="h-7 w-7 p-0 text-[#64748B] hover:text-[#3A86FF]"
                                >
                                  {audioGenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />}
                                </Button>
                                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openNavigation(d.location); }} className="h-7 w-7 p-0 text-[#64748B] hover:text-emerald-600">
                                  <Navigation className="w-3.5 h-3.5" />
                                </Button>
                              </>
                            )}
                            <div className="opacity-0 group-hover:opacity-100 flex">
                              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); isActivity ? handleEditActivity(d) : handleEditTransport(d); }} className="h-7 w-7 p-0 text-[#3A86FF]">
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={async (e) => { e.stopPropagation(); isActivity ? await deleteActivity(item.id) : await deleteTransportSegment(item.id); onRefresh(); }} className="h-7 w-7 p-0 text-red-400">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </SortableItem>
                  );
                })
              )}
            </div>
          </SortableContext>
        </DndContext>

        {/* Edit Activity Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Modifier l'activité</DialogTitle></DialogHeader>
            {editingActivity && (
              <div className="space-y-3 mt-4">
                <div><Label>Titre</Label><Input value={editingActivity.title || ''} onChange={e => setEditingActivity({...editingActivity, title: e.target.value})} className="mt-1" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Créneau</Label>
                    <Select value={editingActivity.time_block || 'morning'} onValueChange={v => setEditingActivity({...editingActivity, time_block: v})}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{timeBlocks.map(b => <SelectItem key={b.key} value={b.key}>{b.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Lieu</Label><Input value={editingActivity.location || ''} onChange={e => setEditingActivity({...editingActivity, location: e.target.value})} className="mt-1" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Début</Label><Input type="time" value={editingActivity.start_time || ''} onChange={e => setEditingActivity({...editingActivity, start_time: e.target.value})} className="mt-1" /></div>
                  <div><Label>Fin</Label><Input type="time" value={editingActivity.end_time || ''} onChange={e => setEditingActivity({...editingActivity, end_time: e.target.value})} className="mt-1" /></div>
                </div>
                <div><Label>Notes</Label><Textarea value={editingActivity.notes || ''} onChange={e => setEditingActivity({...editingActivity, notes: e.target.value})} className="mt-1" rows={2} /></div>
                <Button onClick={handleSaveActivity} className="w-full rounded-full bg-[#3A86FF] text-white">Enregistrer</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Transport Dialog */}
        <Dialog open={editTransOpen} onOpenChange={setEditTransOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Modifier le transport</DialogTitle></DialogHeader>
            {editingTransport && (
              <div className="space-y-3 mt-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Départ</Label><Input value={editingTransport.from_location || ''} onChange={e => setEditingTransport({...editingTransport, from_location: e.target.value})} className="mt-1" /></div>
                  <div><Label>Arrivée</Label><Input value={editingTransport.to_location || ''} onChange={e => setEditingTransport({...editingTransport, to_location: e.target.value})} className="mt-1" /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Mode</Label>
                    <Select value={editingTransport.mode || 'driving'} onValueChange={v => setEditingTransport({...editingTransport, mode: v})}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{transportModes.map(m => <SelectItem key={m.value} value={m.value}><span className="flex items-center gap-2"><m.icon className="w-4 h-4" />{m.label}</span></SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>H. départ</Label><Input type="time" value={editingTransport.departure_time || ''} onChange={e => setEditingTransport({...editingTransport, departure_time: e.target.value})} className="mt-1" /></div>
                  <div><Label>H. arrivée</Label><Input type="time" value={editingTransport.arrival_time || ''} onChange={e => setEditingTransport({...editingTransport, arrival_time: e.target.value})} className="mt-1" /></div>
                </div>
                <Button onClick={handleSaveTransport} className="w-full rounded-full bg-emerald-500 text-white">Enregistrer</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Detail Sheet - Force re-render with key */}
        <Sheet 
          key={detailItem?.activity_id || detailItem?.transport_id || 'empty'} 
          open={detailOpen} 
          onOpenChange={setDetailOpen}
        >
          <SheetContent side="right" className="w-[400px] sm:w-[540px]">
            <SheetHeader>
              <SheetTitle>{detailItem?.from_location ? 'Transport' : detailItem?.title}</SheetTitle>
              <SheetDescription>
                {detailItem?.from_location ? 'Détails du transport' : 'Détails de l\'activité'}
              </SheetDescription>
            </SheetHeader>
            {detailItem && (
              <div className="mt-6 space-y-4">
                {detailItem.from_location ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-xl">
                      <div className="text-center flex-1">
                        <p className="text-lg font-bold text-emerald-700">{detailItem.departure_time || '--:--'}</p>
                        <p className="text-xs text-emerald-600">{detailItem.from_location}</p>
                      </div>
                      <ArrowRight className="w-6 h-6 text-emerald-400" />
                      <div className="text-center flex-1">
                        <p className="text-lg font-bold text-emerald-700">{detailItem.arrival_time || '--:--'}</p>
                        <p className="text-xs text-emerald-600">{detailItem.to_location}</p>
                      </div>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700 border-0">{getModeLabel(detailItem.mode)}</Badge>
                    <Button onClick={() => openNavigation(detailItem.to_location)} className="w-full rounded-xl bg-emerald-500 text-white">
                      <Navigation className="w-4 h-4 mr-2" /> Ouvrir dans Maps
                    </Button>
                    <Button onClick={() => handleEditTransport(detailItem)} variant="outline" className="w-full rounded-xl">
                      <Pencil className="w-4 h-4 mr-2" /> Modifier
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Always show title */}
                    <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl">
                      <h3 className="font-semibold text-purple-900">{detailItem.title}</h3>
                      {detailItem.time_block && (
                        <p className="text-xs text-purple-600 mt-1 capitalize">{detailItem.time_block}</p>
                      )}
                    </div>
                    
                    {detailItem.location && (
                      <div className="p-4 bg-[#F8FAFC] rounded-xl">
                        <p className="text-xs text-[#64748B] uppercase mb-1">Lieu</p>
                        <p className="text-sm font-medium">{detailItem.location}</p>
                      </div>
                    )}
                    {(detailItem.start_time || detailItem.end_time) && (
                      <div className="flex gap-4">
                        {detailItem.start_time && <div className="flex-1 p-3 bg-[#F8FAFC] rounded-xl text-center"><p className="text-xs text-[#64748B]">Début</p><p className="text-lg font-bold text-[#3A86FF]">{detailItem.start_time}</p></div>}
                        {detailItem.end_time && <div className="flex-1 p-3 bg-[#F8FAFC] rounded-xl text-center"><p className="text-xs text-[#64748B]">Fin</p><p className="text-lg font-bold text-[#3A86FF]">{detailItem.end_time}</p></div>}
                      </div>
                    )}
                    {detailItem.notes && <div className="p-4 bg-amber-50 rounded-xl"><p className="text-xs text-amber-600 uppercase mb-1">Notes</p><p className="text-sm">{detailItem.notes}</p></div>}
                    {detailItem.guide_text && <div className="p-4 bg-[#EBF2FF] rounded-xl max-h-48 overflow-y-auto"><p className="text-xs text-[#3A86FF] uppercase mb-2">Guide Audio</p><p className="text-sm whitespace-pre-wrap">{detailItem.guide_text}</p></div>}
                    
                    {/* INFO: No location yet */}
                    {!detailItem.location && !detailItem.start_time && !detailItem.notes && (
                      <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                        <p className="text-sm text-blue-800">
                          ℹ️ Cette activité n'a pas encore de détails (lieu, horaires). 
                          Cliquez sur "Modifier" pour les ajouter.
                        </p>
                      </div>
                    )}
                    
                    {/* ALWAYS show action buttons */}
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => handlePlayAudioGuide(detailItem)} 
                        variant="outline" 
                        className="flex-1 rounded-xl border-[#3A86FF] text-[#3A86FF]" 
                        disabled={audioGenerating || !detailItem.location}
                        title={!detailItem.location ? "Ajoutez un lieu pour générer l'audio" : "Générer guide audio"}
                      >
                        {audioGenerating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Volume2 className="w-4 h-4 mr-2" />}
                        Audio {!detailItem.location && "🔒"}
                      </Button>
                      <Button 
                        onClick={() => detailItem.location && window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(detailItem.location)}`, '_blank')} 
                        className="flex-1 rounded-xl bg-emerald-500 text-white"
                        disabled={!detailItem.location}
                        title={!detailItem.location ? "Ajoutez un lieu pour naviguer" : "Ouvrir dans Google Maps"}
                      >
                        <Navigation className="w-4 h-4 mr-2" />
                        GPS {!detailItem.location && "🔒"}
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => handleEditActivity(detailItem)} variant="default" className="flex-1 rounded-xl bg-purple-600 hover:bg-purple-700">
                        <Pencil className="w-4 h-4 mr-2" />Modifier & Compléter
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </SheetContent>
        </Sheet>
      </CardContent>


      {/* AI Chat Modal */}
      <DayAIChatModal dayPlanId={plan.day_plan_id} open={aiChatOpen} onClose={() => setAiChatOpen(false)} />
    </Card>
  );
}
